import { useMemo, useState, type FC } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, StickyNote2Outlined } from '@mui/icons-material';

import {
  ECM_DEFINITIONS,
  type EcmDefinition,
} from '#apps/client-web/features/audit-form-ecms/constants';

import { type AuditEcmsForm } from '#packages/data-collection/models/audit-ecms-form';

import { type EditAuditFormEcms } from './types';

const computeIncludedIds = (
  ecmsData: AuditEcmsForm['ecms'] | undefined,
): Set<string> => {
  const included = new Set<string>();
  if (!ecmsData) return included;
  for (const ecmEntry of ecmsData) {
    const ecmDef = ECM_DEFINITIONS.find((e) => e.id === ecmEntry.id);
    if (!ecmDef || ecmDef.criteria.length === 0) continue;
    if (
      ecmEntry.criteria.length === ecmDef.criteria.length &&
      ecmEntry.criteria.every((c) => c?.answer === true)
    ) {
      included.add(ecmEntry.id);
    }
  }
  return included;
};

type EcmCriterionRowProps = {
  ecmIndex: number;
  criterionIndex: number;
  description: string;
  form: EditAuditFormEcms;
  isPending: boolean;
};

const EcmCriterionRow: FC<EcmCriterionRowProps> = ({
  ecmIndex,
  criterionIndex,
  description,
  form,
  isPending,
}) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const noteValue = useWatch({
    control: form.control,
    name: `ecms.${ecmIndex}.criteria.${criterionIndex}.notes`,
  });
  const hasNote = Boolean(noteValue);

  return (
    <Box>
      <ListItem disablePadding sx={{ py: 1.5, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ flex: 1, pr: 0.5 }}>
          {description}
        </Typography>
        <Controller
          control={form.control}
          name={`ecms.${ecmIndex}.criteria.${criterionIndex}.answer`}
          disabled={isPending}
          render={({ field }) => (
            <FormControl sx={{ flexShrink: 0 }}>
              <Switch
                {...field}
                checked={field.value ?? false}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            </FormControl>
          )}
        />
        <IconButton
          size="small"
          sx={{
            flexShrink: 0,
            color: hasNote ? 'primary.main' : 'text.disabled',
          }}
          onClick={() => setNoteOpen((prev) => !prev)}>
          <StickyNote2Outlined fontSize="small" />
        </IconButton>
      </ListItem>
      <Collapse in={noteOpen} unmountOnExit>
        <Box sx={{ mb: 2 }}>
          <Controller
            control={form.control}
            name={`ecms.${ecmIndex}.criteria.${criterionIndex}.notes`}
            disabled={isPending}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Notes"
                multiline
                minRows={2}
                maxRows={5}
                size="small"
                fullWidth
              />
            )}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

type EcmCriteriaListProps = {
  ecm: EcmDefinition;
  form: EditAuditFormEcms;
  isPending: boolean;
};

const EcmCriteriaList: FC<EcmCriteriaListProps> = ({
  ecm,
  form,
  isPending,
}) => {
  const ecmIndex = ECM_DEFINITIONS.findIndex((e) => e.id === ecm.id);
  return (
    <List>
      {ecm.criteria.map((criterion, i) => (
        <Box key={i}>
          {i > 0 && <Divider />}
          <EcmCriterionRow
            ecmIndex={ecmIndex}
            criterionIndex={i}
            description={criterion.description}
            form={form}
            isPending={isPending}
          />
        </Box>
      ))}
    </List>
  );
};

export type AuditFormEcmsPageRendererProps = {
  form: EditAuditFormEcms;
  isPending: boolean;
  onSubmit: () => void;
};

export const AuditFormEcmsPageRenderer: FC<AuditFormEcmsPageRendererProps> = ({
  form,
  isPending,
  onSubmit,
}) => {
  const ecmsData = useWatch({ control: form.control, name: 'ecms' });
  const includedIds = useMemo(() => computeIncludedIds(ecmsData), [ecmsData]);

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      {ECM_DEFINITIONS.map((ecmDef) => (
        <Box sx={{ mt: 2 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography fontWeight="bold" variant="subtitle2">
              {ecmDef.label}
            </Typography>
            {includedIds.has(ecmDef.id) && (
              <Grid container alignItems="center" spacing={0.25}>
                <CheckCircle
                  sx={{
                    fontSize: 16,
                    color: 'primary.main',
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'primary.main',
                  }}>
                  Included
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid flexGrow={1}>
            <Divider
              sx={{
                borderWidth: 1,
              }}
            />
          </Grid>
          <EcmCriteriaList
            key={ecmDef.id}
            ecm={ecmDef}
            form={form}
            isPending={isPending}
          />
        </Box>
      ))}
    </Box>
  );
};
